import { createKeycloak, initKeycloak } from 'src/auth/keycloak'
import { environment } from 'src/environments/environment'
import router from 'src/router'
import { useStore } from 'src/store'
import Keycloak, { KeycloakConfig } from 'keycloak-js'

const keycloakServices: { [key: string]: Keycloak } = {}

export function getRealm() {
  return localStorage.getItem('realmId')
}

export async function masterKeycloakSetup() {
  createKeycloak({
    url: environment.keycloakUrl,
    realm: 'zdb',
    clientId: environment.keycloakClientId
  })
  await initKeycloak({
    flow: 'standard',
    checkLoginIframe: true,
    onLoad: 'login-required'
  })
}

export async function requireKeycloakService(realm: string): Promise<Keycloak> {
  if (!keycloakServices[realm]) {
    keycloakServices[realm] = await initializeKeycloak(realm)
  }
  return keycloakServices[realm]
}

export function getTokenExpirationTime(realm: string) {
  return keycloakServices[realm].tokenParsed?.exp
}
export async function initializeKeycloak(realm: string): Promise<Keycloak> {
  const config: KeycloakConfig = { url: environment.keycloakUrl, realm: realm, clientId: environment.keycloakClientId }
  keycloakServices[realm] = new Keycloak(config)
  await keycloakServices[realm].init({ enableLogging: true, flow: 'standard' })
  console.log('Realm: ' + realm + ' Auth: ' + keycloakServices[realm].authenticated)
  return keycloakServices[realm]
}

export async function login(): Promise<void> {
  const realm = getRealm()
  if (!realm) {
    router.push('/realm')
    return
  }

  const keycloakService = await requireKeycloakService(realm)
  const store = useStore()
  store.updateLoginStatus(true)
  await keycloakService.login({
    redirectUri: window.location.origin,
    scope: 'roles openid',
    idpHint: 'digital-buildings'
  })
}

export async function logout(): Promise<void> {
  const store = useStore()
  store.updateLoginStatus(false)
  const realm = getRealm()
  if (!realm) return

  localStorage.clear()
  store.$reset()
  return (await requireKeycloakService(realm)).logout({ redirectUri: window.location.origin })
}

export async function isLoggedIn(): Promise<boolean> {
  const realm = getRealm()
  if (!realm) {
    return false
  } else if (!(await requireKeycloakService(realm)).authenticated) {
    await login()
    return true
  }
  return true
}

export function roles(): string[] {
  const realm = getRealm()
  if (!realm) return []
  return keycloakServices[realm].realmAccess?.roles ?? []
}

export function token(): string | undefined {
  const realm = getRealm()
  if (!realm) return undefined
  if (!keycloakServices[realm] || keycloakServices[realm].isTokenExpired()) {
    login()
    return undefined
  } else {
    return keycloakServices[realm].token
  }
}

export async function updateToken(realm: string) {
  if (!keycloakServices[realm]) return
  await keycloakServices[realm]
    .updateToken(60 * 5) // refresh token if it will expire in 5 minutes or less
    .then(function (refreshed) {
      if (refreshed) {
        console.debug('Token refreshed' + refreshed)
      } else {
        console.warn('Token not refreshed, logging out')
        logout()
      }
    })
    .catch(function () {
      console.error('Failed to refresh token, logging out')
      logout()
    })
  return keycloakServices[realm].token as string
}

// MARK: Helper function(s)

/**
 *
 * @param epoch number
 * @returns number
 * @description Calculate the difference in seconds between the given epoch and the current time
 * epoch:   The epoch time to compare to the current time
 * returns: The difference in seconds between the given epoch and the current time
 *         If the epoch is in the future, the difference will be negative
 */
export function differenceInSecondsFromNow(epoch: number): number {
  // Current time in milliseconds
  const currentTimeMillis = Date.now()
  // Convert the given epoch from seconds to milliseconds
  const epochMillis = epoch * 1000
  // Calculate the difference in milliseconds
  const differenceMillis = epochMillis - currentTimeMillis
  // Convert the difference to seconds and return
  return differenceMillis / 1000
}
